<template>
  <div class="relative m-6">
    <!-- Header section -->
    <div class="mb-8 sm:flex sm:items-center">
      <div class="flex flex-auto items-center justify-between">
        <div class="flex items-center space-x-5">
          <div class="shrink-0">
            <div class="relative rounded-xl bg-orange-600/70 p-1">
              <mdicon name="chart-bar" size="62" class="text-orange-100" aria-hidden="true"></mdicon>
            </div>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Analyze Jobs</h1>
            <p class="text-sm font-medium text-gray-500">Analyze jobs in your account</p>
          </div>
        </div>
        <!-- Save Button -->
        <button
          :disabled="!hasChanges"
          severity="primary"
          autofocus
          :class="[
            'mr-2 inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
            hasChanges ? 'bg-indigo-600 hover:bg-indigo-500' : 'cursor-not-allowed bg-indigo-400',
          ]"
          @click="onSave"
        >
          Save
        </button>
      </div>
    </div>

    <!-- Content section -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div v-for="prop in analyzeProps" :key="prop.name">
        <div>
          <label class="block mb-1 text-sm font-medium leading-6 text-gray-900">
            {{ prop.title }}
          </label>
          <div class="flex items-center">
            <Dropdown
              v-model="configurationData[prop.name]"
              :options="prop.name === 'ingestJobId' || prop.name === 'qaJobId' ? analyzeRecordingsJobs : analyzeTranscriptsJobs"
              filter
              option-label="name"
              option-value="id"
              :placeholder="`Select ${prop.title}`"
              class="md:w-14rem w-full"
            />
            <Button v-tooltip.top="'Clear'" icon="pi pi-times" severity="secondary" class="ml-2" @click="configurationData[prop.name] = null" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted, watch, computed } from 'vue';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { useJobsStore } from '@/store/JobsStore';
  import { useToast } from 'vue-toastification';
  import Dropdown from 'primevue/dropdown';
  import Button from 'primevue/button';

  const settingsStore = useSettingsStore();
  const jobsStore = useJobsStore();
  const toast = useToast();
  const analyzeRecordingsJobs = ref([]);
  const analyzeTranscriptsJobs = ref([]);

  watch(
    () => jobsStore.jobs,
    (jobs) => {
      if (Array.isArray(jobs) && jobs.length > 1) {
        analyzeRecordingsJobs.value = jobs.filter((job) => job.type === 'analyzeRecordings');
        analyzeTranscriptsJobs.value = jobs.filter((job) => job.type === 'analyzeTranscripts');
      }
    },
    { immediate: true },
  );

  let configurationData = reactive({
    ingestJobId: '',
    qaJobId: '',
    transcriptIngestJobId: '',
    transcriptQAJobId: '',
  });

  const originalConfiguration = reactive({
    ingestJobId: '',
    qaJobId: '',
    transcriptIngestJobId: '',
    transcriptQAJobId: '',
  });

  const hasChanges = computed(() => {
    const current = JSON.stringify(configurationData);
    const original = JSON.stringify(originalConfiguration);
    console.log('hasChanges:', current !== original);
    return current !== original;
  });

  const analyzeProps = [
    { name: 'ingestJobId', type: 'string', title: 'Analyze Recordings Ingest Job', default: '' },
    { name: 'transcriptIngestJobId', type: 'string', title: 'Analyze Transcripts Ingest Job', default: '' },
    { name: 'qaJobId', type: 'string', title: 'Analyze Recordings QA Job', default: '' },
    { name: 'transcriptQAJobId', type: 'string', title: 'Analyze Transcripts QA Job', default: '' },
  ];

  const jobKeys = ['ingestJobId', 'qaJobId', 'transcriptIngestJobId', 'transcriptQAJobId'];

  async function onSave() {
    for (const key of jobKeys) {
      await settingsStore.setSettings(key, configurationData[key] || '');
    }
    toast.success(`Analyze jobs saved successfully.`);
  }

  onMounted(async () => {
    jobKeys.forEach((key) => {
      const value = settingsStore.tenantSettings[key];
      configurationData[key] = value;
      originalConfiguration[key] = value;
    });
  });
</script>
